import { isSaaSMode } from './runtimeConfig';

const DASHBOARD_ORIGIN = 'https://dashboard.hermetiq.io';
const MARKETING_ORIGIN = 'https://www.hermetiq.com';

export const ATTRIBUTION_KEY = 'hermetiq-acquisition';
export const ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const NEW_ACCOUNT_WINDOW_MS = 60 * 60 * 1000;
const CONVERSION_KEY_PREFIX = 'hermetiq-conversion';
const ALLOWED_LEAD_TYPES = new Set(['organic', 'outbound', 'evaluator', 'internal', 'unknown']);
const EVALUATOR_CAMPAIGNS = new Set(['vc_evaluation', 'vc_evaluator', 'vc_review']);

function clean(value, maxLength) {
  return String(value || '').trim().slice(0, maxLength);
}

function readStorage(key) {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStorage(key, value) {
  if (typeof window === 'undefined') return false;

  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
}

function removeStorage(key) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // Attribution must never interrupt authentication.
  }
}

export function readHermetiqAttribution(search, depth = 0, now = Date.now()) {
  if (depth > 2) return null;

  const params = new URLSearchParams(search || '');
  const redirectSearch = params.get('redirect_url');

  if (params.get('utm_source') !== 'hermetiq.com' && redirectSearch) {
    try {
      const redirectUrl = new URL(redirectSearch, DASHBOARD_ORIGIN);
      return readHermetiqAttribution(redirectUrl.search, depth + 1, now);
    } catch (error) {
      return null;
    }
  }

  if (params.get('utm_source') !== 'hermetiq.com') return null;

  return {
    campaign: clean(params.get('utm_campaign'), 120) || 'create_account',
    placement: clean(params.get('utm_content'), 160) || 'unspecified',
    capturedAt: now,
  };
}

export function storeHermetiqAttribution(attribution) {
  if (!attribution?.capturedAt) return false;
  return writeStorage(ATTRIBUTION_KEY, JSON.stringify(attribution));
}

export function getStoredHermetiqAttribution(now = Date.now()) {
  try {
    const attribution = JSON.parse(readStorage(ATTRIBUTION_KEY) || 'null');
    if (!attribution?.capturedAt || now - Number(attribution.capturedAt) > ATTRIBUTION_TTL_MS) {
      removeStorage(ATTRIBUTION_KEY);
      return null;
    }

    return {
      campaign: clean(attribution.campaign, 120) || 'create_account',
      placement: clean(attribution.placement, 160) || 'unspecified',
      capturedAt: Number(attribution.capturedAt),
    };
  } catch (error) {
    removeStorage(ATTRIBUTION_KEY);
    return null;
  }
}

export function getActiveHermetiqAttribution(search, now = Date.now()) {
  return readHermetiqAttribution(search, 0, now) || getStoredHermetiqAttribution(now);
}

export function isHostedDashboardOrigin(origin) {
  return clean(origin, 255) === DASHBOARD_ORIGIN;
}

export function isNewlyCreatedUser(user, now = Date.now()) {
  const createdAt = Date.parse(user?.created_at || user?.createdAt || '');
  return Number.isFinite(createdAt) && now - createdAt >= 0 && now - createdAt <= NEW_ACCOUNT_WINDOW_MS;
}

function userEmail(user) {
  return clean(user?.email || user?.emails?.[0]?.email, 255).toLowerCase();
}

export function classifyLeadType(user, attribution) {
  const trustedMetadata = user?.trusted_metadata || {};
  const explicitType = clean(
    trustedMetadata.analytics_lead_type || trustedMetadata.lead_type,
    40
  ).toLowerCase();

  if (ALLOWED_LEAD_TYPES.has(explicitType)) return explicitType;

  const email = userEmail(user);
  if (email.endsWith('@hermetiq.com')) return 'internal';

  const campaign = clean(attribution?.campaign, 120).toLowerCase();
  if (EVALUATOR_CAMPAIGNS.has(campaign)) {
    return 'evaluator';
  }

  return 'unknown';
}

function userDisplayName(user) {
  if (typeof user?.name === 'string') return clean(user.name, 255);

  const firstName = clean(user?.name?.first_name || user?.first_name, 120);
  const lastName = clean(user?.name?.last_name || user?.last_name, 120);
  return clean(`${firstName} ${lastName}`, 255);
}

export function buildLogRocketIdentity(user, attribution) {
  const uid = clean(user?.user_id || user?.id, 255);
  if (!uid) return null;

  const traits = {
    authSource: clean(user?.auth_source, 80) || 'unknown',
    leadType: classifyLeadType(user, attribution),
  };
  const email = userEmail(user);
  const name = userDisplayName(user);

  if (email) traits.email = email;
  if (name) traits.name = name;
  if (attribution?.campaign) traits.acquisitionCampaign = clean(attribution.campaign, 120);
  if (attribution?.placement) traits.acquisitionPlacement = clean(attribution.placement, 160);

  return { uid, traits };
}

export function buildAcquisitionEventPayload(event, attribution, leadType = 'unknown') {
  return {
    event: clean(event, 80),
    campaign: clean(attribution?.campaign, 120) || 'unknown',
    placement: clean(attribution?.placement, 160) || 'unknown',
    leadType: clean(leadType, 40) || 'unknown',
  };
}

export async function recordAcquisitionEvent(event, attribution, options = {}) {
  if (
    typeof window === 'undefined' ||
    !isSaaSMode ||
    !isHostedDashboardOrigin(window.location.origin) ||
    typeof window.fetch !== 'function' ||
    !attribution
  ) {
    return false;
  }

  const eventName = clean(event, 80);
  if (!eventName) return false;

  const leadType = clean(options.leadType, 40) || 'unknown';
  const onceKey = clean(options.onceKey, 300);
  const storageKey = onceKey
    ? `${CONVERSION_KEY_PREFIX}:${eventName}:${onceKey}`
    : '';

  if (storageKey && readStorage(storageKey)) return true;

  try {
    const response = await window.fetch(`${MARKETING_ORIGIN}/api/conversion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildAcquisitionEventPayload(eventName, attribution, leadType)),
      credentials: 'omit',
      keepalive: true,
    });

    if (!response.ok) return false;
    if (storageKey) writeStorage(storageKey, new Date().toISOString());
    return true;
  } catch (error) {
    return false;
  }
}
